import apiClient from './client';
import type {
  QASession, ClassificationSuggestion, DocumentEntities,
  GraphData, RelatedDocument, GraphStats,
} from '../types/knowledge';

const BASE = '/knowledge';

// ─── RAG Q&A ─────────────────────────────────────────────────────────────────

/**
 * Ask a question via SSE streaming.
 * Yields raw SSE lines — caller should parse data: {...} lines.
 */
export async function* askQuestion(
  question: string,
  sessionId?: string,
): AsyncGenerator<string> {
  const token = localStorage.getItem('access_token');
  const res = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}${BASE}/ask`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ question, session_id: sessionId }),
    },
  );

  if (!res.ok) throw new Error(`Q&A request failed: ${res.status}`);
  if (!res.body) throw new Error('No response body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (line.startsWith('data: ')) yield line.slice(6);
    }
  }
}

export const getQAHistory = (limit = 20, offset = 0) =>
  apiClient.get<QASession[]>(`${BASE}/ask/history`, { params: { limit, offset } })
    .then(r => r.data);

// ─── Document AI Features ─────────────────────────────────────────────────────

export const getSummary = (docId: string) =>
  apiClient.get<{ doc_id: string; summary: string }>(`${BASE}/documents/${docId}/summary`)
    .then(r => r.data);

export const triggerSummarize = (docId: string) =>
  apiClient.post(`${BASE}/documents/${docId}/summarize`).then(r => r.data);

export const getEntities = (docId: string) =>
  apiClient.get<DocumentEntities>(`${BASE}/documents/${docId}/entities`).then(r => r.data);

export const getClassificationSuggestion = (docId: string) =>
  apiClient.get<ClassificationSuggestion>(`${BASE}/documents/${docId}/classify`)
    .then(r => r.data);

export const reprocessDocument = (docId: string, steps: string[] = ['all']) =>
  apiClient.post(`${BASE}/documents/${docId}/reprocess`, { steps }).then(r => r.data);

export interface ApplyClassificationBody {
  accept_fields: string[];
  overrides?: Record<string, string | string[]>;
}

export const applyClassification = (docId: string, body: ApplyClassificationBody) =>
  apiClient
    .post(`${BASE}/documents/${docId}/classification/apply`, body)
    .then(r => r.data);

export const rejectClassification = (docId: string, alasan?: string) =>
  apiClient
    .post(`${BASE}/documents/${docId}/classification/reject`, { alasan })
    .then(r => r.data);

// ─── Graph ────────────────────────────────────────────────────────────────────

export const getDocumentGraph = (docId: string, depth = 2) =>
  apiClient.get<GraphData>(`/graph/documents/${docId}`, { params: { depth } }).then(r => r.data);

export const getRelatedDocuments = (docId: string) =>
  apiClient.get<RelatedDocument[]>(`/graph/documents/${docId}/related`).then(r => r.data);

export const getEntityNetwork = (entityName: string) =>
  apiClient.get<GraphData>(`/graph/entities/${encodeURIComponent(entityName)}`).then(r => r.data);

export const getRegulationTree = (regNomor: string) =>
  apiClient.get<GraphData>(`/graph/regulations/${encodeURIComponent(regNomor)}`).then(r => r.data);

export const getGraphStats = () =>
  apiClient.get<GraphStats>('/graph/stats').then(r => r.data);

// ─── Fragments & Chunks ──────────────────────────────────────────────────────

export interface DocumentFragmentNode {
  id: string;
  dokumen_id: string;
  parent_fragment_id?: string | null;
  fragment_type: string;
  nomor?: string | null;
  judul?: string | null;
  teks?: string | null;
  urutan: number;
  level: number;
  path?: string | null;
  page_start?: number | null;
  page_end?: number | null;
  children: DocumentFragmentNode[];
}

export const getDocumentFragments = (docId: string) =>
  apiClient
    .get<DocumentFragmentNode[]>(`/documents/${docId}/fragments`)
    .then(r => r.data);

export interface DocumentChunkItem {
  id: string;
  chunk_index: number;
  chunk_text: string;
  token_count: number;
}

export interface DocumentChunksResponse {
  doc_id: string;
  items: DocumentChunkItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const getDocumentChunks = (docId: string, page = 1, pageSize = 25) =>
  apiClient
    .get<DocumentChunksResponse>(`${BASE}/documents/${docId}/chunks`, {
      params: { page, page_size: pageSize },
    })
    .then(r => r.data);

// ─── Citations ────────────────────────────────────────────────────────────────

export interface DocumentCitationItem {
  target_id?: string;
  source_id?: string;
  judul: string;
  nomor_dokumen?: string | null;
  confidence: number;
  match_method: string;
  context_sentence?: string | null;
  source_pasal?: string | null;
  normalized?: string | null;
}

export interface DocumentCitations {
  outgoing: DocumentCitationItem[];
  incoming: DocumentCitationItem[];
}

export const getDocumentCitations = (docId: string) =>
  apiClient
    .get<DocumentCitations>(`/graph/documents/${docId}/citations`)
    .then(r => r.data);
